import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PrimaryNavRail } from '../../components/map/PrimaryNavRail';
import { Logo } from '../../components/brand/Logo';
import { BasemapsTab } from '../../components/admin/BasemapsTab';
import { LayerGroupsTab } from '../../components/admin/LayerGroupsTab';
import { SearchTab } from '../../components/admin/SearchTab';
import { LegendTab } from '../../components/admin/LegendTab';
import { AccessTab } from '../../components/admin/AccessTab';
import { useAdminData } from '../../admin/AdminDataContext';
import { defaultMapSettingsTemplate } from '../../admin/store';
import { TerritoryPreviewMap } from '../../admin/TerritoryPreviewMap';
import type { MapSettings, MapToolKey } from '../../admin/types';
import { api, ApiError } from '../../lib/api';
import { mapsApi, type GeoserverInfo } from '../../lib/mapsApi';
import type { Me } from '../../lib/types';

function initialsOf(me: Me | null): string {
  if (!me) return '…';
  const a = me.first_name?.[0] ?? '';
  const b = me.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || '??';
}

function autoCodeFromOrgName(orgName: string): string {
  const letters = orgName.trim().slice(0, 3);
  if (!letters) return '';
  const normalized = letters[0].toUpperCase() + letters.slice(1).toLowerCase();
  return `${normalized}-001`;
}

const behaviorLabels: Record<keyof MapSettings['behavior'], string> = {
  rememberUserPosition: 'Запоминать позицию пользователя',
  restrictPanToExtent: 'Ограничить перемещение территорией',
  autoOpenLegend: 'Автоматически открывать легенду',
  persistActiveLayers: 'Сохранять набор активных слоёв',
  showCursorCoordinates: 'Показывать координаты курсора',
};

const toolLabels: Record<MapToolKey, string> = {
  search: 'Поиск объектов',
  legend: 'Легенда',
  share: 'Поделиться',
  boxSelect: 'Выбор рамкой',
  measure: 'Измерение (расстояние / площадь / периметр)',
  locate: 'Моё местоположение',
  coordinates: 'Ввод координат',
  editFeatures: 'Редактирование объектов',
  spatialQuery: 'Пространственные запросы',
  clustering: 'Кластеризация',
  objectCalculator: 'Объектный калькулятор',
  exportSelection: 'Экспорт выборки',
  printMap: 'Печать карты',
  aiAnalysis: 'AI-анализ территории',
  rosreestr: 'Роскадастр (границы участков)',
};

const tabs = ['Основные', 'Подложки', 'Слои и группы', 'Поиск', 'Легенда', 'Доступ'];

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`flex h-[26px] w-[52px] shrink-0 items-center rounded-full px-1 transition-colors ${
        checked ? 'bg-action-primary justify-end' : 'bg-bg-surface-secondary justify-start'
      }`}
    >
      <span className="size-[18px] rounded-full bg-bg-surface shadow" />
    </button>
  );
}

export function MapSettingsPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const { basemaps, mapSettings, mapSettingsLoading, updateMapSettings, createMap } = useAdminData();
  const isCreating = mapSettings === null;
  const [form, setForm] = useState<MapSettings>(() => mapSettings ?? { ...defaultMapSettingsTemplate });
  // mapSettings loads asynchronously (AdminDataContext) — the lazy useState above only
  // runs on first render, so if it mounts before that fetch resolves, `form` gets seeded
  // from the all-enabled defaultMapSettingsTemplate and never updates once the real,
  // possibly-different persisted settings arrive. That's what caused toggles to display
  // as ON in this tab while MapControls (which reads mapSettings directly, not `form`)
  // correctly hid the actually-disabled tools. Sync `form` exactly once, right when the
  // initial fetch settles — for a genuinely new map (mapSettings stays null) this is a
  // no-op and the defaults are correctly kept.
  const [formSynced, setFormSynced] = useState(false);
  useEffect(() => {
    if (formSynced || mapSettingsLoading) return;
    if (mapSettings) setForm(mapSettings);
    setFormSynced(true);
  }, [mapSettingsLoading, mapSettings, formSynced]);
  const [activeTab, setActiveTab] = useState('Основные');
  const [drawMode, setDrawMode] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [flyTo, setFlyTo] = useState<{ center: [number, number]; zoom: number; token: number } | null>(null);
  const [geoserverInfo, setGeoserverInfo] = useState<GeoserverInfo | null>(null);

  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
    mapsApi.getGeoserverInfo().then(setGeoserverInfo).catch(() => setGeoserverInfo(null));
  }, []);

  useEffect(() => {
    if (!isCreating) return;
    const orgName = me?.workspace?.organization_name;
    if (!orgName) return;
    setForm((prev) => (prev.code.trim() ? prev : { ...prev, code: autoCodeFromOrgName(orgName) }));
  }, [me, isCreating]);

  const bbox: [number, number, number, number] = [
    form.initialView.minLon,
    form.initialView.minLat,
    form.initialView.maxLon,
    form.initialView.maxLat,
  ];

  const handleViewChange = (center: [number, number], zoom: number) => {
    setForm((prev) => ({
      ...prev,
      initialView: { ...prev.initialView, centerLon: center[0], centerLat: center[1], zoom },
    }));
  };

  const handleBoxDrawn = ([minLon, minLat, maxLon, maxLat]: [number, number, number, number]) => {
    setForm((prev) => ({ ...prev, initialView: { ...prev.initialView, minLon, minLat, maxLon, maxLat } }));
    setDrawMode(false);
  };

  const handleResetBounds = () => {
    setForm((prev) => ({
      ...prev,
      initialView: { ...prev.initialView, minLat: prev.initialView.centerLat - 0.05, minLon: prev.initialView.centerLon - 0.08, maxLat: prev.initialView.centerLat + 0.05, maxLon: prev.initialView.centerLon + 0.08 },
    }));
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      if (isCreating) {
        if (!form.title.trim()) return;
        await createMap(form);
        navigate('/app');
        return;
      }
      const saved = await updateMapSettings(form);
      setForm(saved);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить карту.');
    } finally {
      setSaving(false);
    }
  };

  const toggleBehavior = (key: keyof MapSettings['behavior']) =>
    setForm((prev) => ({ ...prev, behavior: { ...prev.behavior, [key]: !prev.behavior[key] } }));

  const toggleTool = (key: MapToolKey) =>
    setForm((prev) => ({ ...prev, tools: { ...prev.tools, [key]: !prev.tools[key] } }));

  return (
    <div className="flex h-screen w-full overflow-hidden bg-bg-canvas">
      <PrimaryNavRail userInitials={initialsOf(me)} userAvatarUrl={me?.avatar_url} />

      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        <header className="sticky top-0 z-20 flex h-[72px] shrink-0 items-center gap-3 border-b border-border-default bg-bg-surface px-8">
          <Logo variant="light" size={18} />
          <span className="text-text-secondary">/</span>
          <p className="text-sm text-text-secondary">{isCreating ? 'Создание карты' : 'Настройки карты'}</p>
          <div className="ml-auto flex items-center gap-3">
            {!isCreating && (
              <Link
                to="/app"
                className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
              >
                Предпросмотр
              </Link>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || (isCreating && !form.title.trim())}
              className="flex h-10 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Сохраняем…' : isCreating ? 'Создать карту' : saved ? 'Сохранено ✓' : 'Сохранить'}
            </button>
          </div>
        </header>

        <div className="px-8 pt-6">
          <div className="flex items-center gap-3">
            <h1 className="text-[26px] font-semibold text-text-primary">
              {isCreating ? 'Новая карта' : `Карта «${form.title}»`}
            </h1>
            {geoserverInfo && (
              <span
                title={geoserverInfo.connected ? geoserverInfo.ows_url : 'Не удалось подключиться к GeoServer'}
                className={`flex h-6 items-center rounded-lg px-2.5 text-[11px] font-medium ${
                  geoserverInfo.connected ? 'bg-status-success text-text-inverse' : 'bg-status-danger text-text-inverse'
                }`}
              >
                {geoserverInfo.connected ? 'Сервис Карт доступен' : 'Сервис Карт недоступен'}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-text-secondary">
            {isCreating
              ? 'Любое пространство начинается с карты — задайте параметры, и она откроется пустой'
              : 'Общие параметры, слои, инструменты и доступ'}
          </p>
          {saveError && <p className="mt-2 text-xs font-medium text-status-danger">{saveError}</p>}

          <div className="mt-4 flex items-center gap-1">
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`flex h-[38px] items-center rounded-[10px] px-5 text-xs font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-action-primary text-text-inverse'
                    : 'text-text-secondary hover:bg-bg-surface-secondary'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'Слои и группы' ? (
          <LayerGroupsTab />
        ) : activeTab === 'Подложки' ? (
          <BasemapsTab />
        ) : activeTab === 'Поиск' ? (
          <SearchTab search={form.search} onChange={(search) => setForm({ ...form, search })} />
        ) : activeTab === 'Легенда' ? (
          <LegendTab legend={form.legend} onChange={(legend) => setForm({ ...form, legend })} />
        ) : activeTab === 'Доступ' ? (
          isCreating ? (
            <div className="px-8 py-16 text-center text-sm text-text-secondary">
              Сначала сохраните карту — публичная ссылка доступна для уже созданных карт.
            </div>
          ) : (
            <AccessTab mapId={form.id!} mapTitle={form.title} />
          )
        ) : activeTab !== 'Основные' ? (
          <div className="px-8 py-16 text-center text-sm text-text-secondary">
            Раздел «{activeTab}» появится в следующих версиях админ-панели.
          </div>
        ) : (
          <div className="grid grid-cols-[550px_1fr_392px] gap-6 px-8 py-6">
            <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-[15px] font-semibold text-text-primary">Основные параметры</h2>

              <label className="mt-4 text-[11px] font-medium text-text-secondary">Название карты</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Например, Городская инфраструктура"
                className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
              />

              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Код</label>
                  <input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Проект</label>
                  <input
                    value={form.project}
                    onChange={(e) => setForm({ ...form, project: e.target.value })}
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
                  />
                </div>
              </div>

              <label className="mt-3 text-[11px] font-medium text-text-secondary">Система координат</label>
              <select
                value={form.crs}
                onChange={(e) => setForm({ ...form, crs: e.target.value })}
                className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
              >
                <option>EPSG:3857 · Web Mercator</option>
                <option>EPSG:4326 · WGS 84</option>
              </select>

              <label className="mt-3 text-[11px] font-medium text-text-secondary">Подложка по умолчанию</label>
              <select
                value={form.defaultBasemapName}
                onChange={(e) => setForm({ ...form, defaultBasemapName: e.target.value })}
                className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
              >
                {basemaps.map((b) => (
                  <option key={b.id}>{b.name}</option>
                ))}
              </select>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Начальный масштаб</label>
                  <input
                    value={form.initialScaleLabel}
                    onChange={(e) => setForm({ ...form, initialScaleLabel: e.target.value })}
                    className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Диапазон zoom</label>
                  <div className="mt-1.5 flex h-10 items-center gap-2">
                    <input
                      type="number"
                      value={form.zoomRange[0]}
                      onChange={(e) => setForm({ ...form, zoomRange: [Number(e.target.value), form.zoomRange[1]] })}
                      className="h-10 w-full rounded-[9px] border border-border-default bg-bg-surface px-2 text-[11px] text-text-primary outline-none focus:border-action-primary"
                    />
                    <span className="text-text-secondary">—</span>
                    <input
                      type="number"
                      value={form.zoomRange[1]}
                      onChange={(e) => setForm({ ...form, zoomRange: [form.zoomRange[0], Number(e.target.value)] })}
                      className="h-10 w-full rounded-[9px] border border-border-default bg-bg-surface px-2 text-[11px] text-text-primary outline-none focus:border-action-primary"
                    />
                  </div>
                </div>
              </div>

              <div className="my-5 h-px bg-border-default" />

              <p className="text-xs font-semibold text-text-primary">Поведение карты</p>
              <div className="mt-3 flex flex-col gap-3.5">
                {(Object.keys(behaviorLabels) as (keyof MapSettings['behavior'])[]).map((key) => (
                  <div key={key} className="flex items-center justify-between gap-4">
                    <span className="text-[11px] font-medium text-text-secondary">{behaviorLabels[key]}</span>
                    <ToggleSwitch checked={form.behavior[key]} onChange={() => toggleBehavior(key)} />
                  </div>
                ))}
              </div>
            </section>

            <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-semibold text-text-primary">Начальный вид и территория</h2>
                <button
                  type="button"
                  onClick={() =>
                    setFlyTo({ center: [form.initialView.centerLon, form.initialView.centerLat], zoom: form.initialView.zoom, token: Date.now() })
                  }
                  className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                  title="Переместить превью к введённым координатам и zoom"
                >
                  Показать на карте
                </button>
              </div>

              <div className="mt-4 h-[430px] overflow-hidden rounded-xl border border-border-default">
                <TerritoryPreviewMap
                  initialCenter={[form.initialView.centerLon, form.initialView.centerLat]}
                  initialZoom={form.initialView.zoom}
                  bbox={bbox}
                  drawMode={drawMode}
                  onViewChange={handleViewChange}
                  onBoxDrawn={handleBoxDrawn}
                  flyTo={flyTo}
                  className="size-full"
                />
              </div>
              {drawMode && (
                <p className="mt-2 text-[11px] font-medium text-action-primary">
                  Растяните прямоугольник на карте, чтобы задать границы территории
                </p>
              )}

              <div className="mt-4 grid grid-cols-3 gap-3">
                <NumberField label="Центр, широта" value={form.initialView.centerLat} onChange={(v) => setForm({ ...form, initialView: { ...form.initialView, centerLat: v } })} />
                <NumberField label="Центр, долгота" value={form.initialView.centerLon} onChange={(v) => setForm({ ...form, initialView: { ...form.initialView, centerLon: v } })} />
                <NumberField label="Zoom" value={form.initialView.zoom} onChange={(v) => setForm({ ...form, initialView: { ...form.initialView, zoom: v } })} step={0.1} />
              </div>

              <div className="mt-3 grid grid-cols-3 gap-3">
                <NumberField label="Минимальная широта" value={form.initialView.minLat} onChange={(v) => setForm({ ...form, initialView: { ...form.initialView, minLat: v } })} />
                <NumberField label="Минимальная долгота" value={form.initialView.minLon} onChange={(v) => setForm({ ...form, initialView: { ...form.initialView, minLon: v } })} />
                <div className="flex flex-col">
                  <label className="text-[11px] font-medium text-text-secondary">Максимальная точка</label>
                  <div className="mt-1.5 flex h-10 items-center rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary">
                    {form.initialView.maxLat.toFixed(4)}, {form.initialView.maxLon.toFixed(4)}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setDrawMode(true)}
                  className={`flex h-10 flex-1 items-center justify-center rounded-[10px] text-[13px] font-medium transition-colors ${
                    drawMode ? 'bg-action-primary-hover text-text-inverse' : 'bg-action-primary text-text-inverse hover:bg-action-primary-hover'
                  }`}
                >
                  {drawMode ? 'Рисуйте на карте…' : 'Выбрать территорию на карте'}
                </button>
                <button
                  type="button"
                  onClick={handleResetBounds}
                  className="flex h-10 flex-1 items-center justify-center rounded-[10px] border border-border-default bg-bg-surface text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
                >
                  Сбросить границы
                </button>
              </div>
            </section>

            <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
              <h2 className="text-[15px] font-semibold text-text-primary">Инструменты карты</h2>
              <p className="mt-1 text-[11px] text-text-secondary">
                Масштабирование, «Исходный масштаб», полноэкранный режим и переключатель подложек всегда доступны и
                здесь не настраиваются.
              </p>
              <div className="mt-4 flex flex-col gap-3">
                {(Object.keys(toolLabels) as MapToolKey[]).map((key) => (
                  <div key={key} className="flex items-center justify-between gap-4">
                    <span className="text-[11px] font-medium text-text-secondary">{toolLabels[key]}</span>
                    <ToggleSwitch checked={form.tools[key]} onChange={() => toggleTool(key)} />
                  </div>
                ))}
              </div>

              <label className="mt-4 text-[11px] font-medium text-text-secondary">Панель по умолчанию</label>
              <select
                value={form.defaultPanel}
                onChange={(e) => setForm({ ...form, defaultPanel: e.target.value as MapSettings['defaultPanel'] })}
                className="mt-1.5 h-[42px] rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
              >
                <option>Слои и легенда</option>
                <option>ИИ-центр</option>
                <option>Ничего</option>
              </select>

              <button
                type="button"
                onClick={() => setActiveTab('Доступ')}
                disabled={isCreating}
                title={isCreating ? 'Сначала сохраните карту' : undefined}
                className="mt-4 flex h-10 items-center justify-center rounded-[10px] border border-border-default bg-bg-surface text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
              >
                Настроить доступ по ссылке
              </button>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  step = 0.0001,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  step?: number;
}) {
  return (
    <div className="flex flex-col">
      <label className="text-[11px] font-medium text-text-secondary">{label}</label>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
      />
    </div>
  );
}
